import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Download, CheckCircle, AlertCircle, Mail, FileText, RefreshCw } from "lucide-react";

const STATUS = {
  pending: { l: "À échoir", c: "bg-slate-200 text-slate-700" },
  paid: { l: "Payé", c: "bg-emerald-200 text-emerald-900" },
  late: { l: "Retard", c: "bg-yellow-200 text-yellow-900" },
  overdue: { l: "EN DEMEURE", c: "bg-red-200 text-red-900 font-bold" },
};
const fmtMoney = (v) => v != null ? `${parseFloat(v).toLocaleString("fr-FR", {minimumFractionDigits: 2})} €` : "—";
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("fr-FR") : "—";

export default function RentsAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [markModal, setMarkModal] = useState(null);
  const [paid, setPaid] = useState({ payment_method: "virement", payment_reference: "", paid_at: "" });

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/rents" + (filter ? `?status=${filter}` : ""));
      setItems(r.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const regen = async () => {
    await api.post("/admin/rents/generate");
    toast.success("Loyers manquants générés");
    load();
  };

  const markPaid = async () => {
    try {
      await api.post(`/admin/rents/${markModal.id}/mark-paid`, paid);
      toast.success("Loyer marqué payé — quittance générée");
      setMarkModal(null); setPaid({ payment_method: "virement", payment_reference: "", paid_at: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec"); }
  };

  const sendReminder = async (id) => {
    try {
      const { data } = await api.post(`/admin/rents/${id}/reminder`);
      toast.success(data.type === "demand" ? "Mise en demeure envoyée" : "Rappel envoyé");
      load();
    } catch (e) { toast.error("Échec"); }
  };

  const downloadDemand = (id) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/rents/${id}/demand-pdf`, "_blank");
  };
  const downloadReceipt = (url) => window.open(`${process.env.REACT_APP_BACKEND_URL}${url}`, "_blank");

  const stats = items.reduce((a, x) => ({ ...a, [x.status]: (a[x.status] || 0) + 1, total_overdue: a.total_overdue + (x.status === "overdue" ? x.amount_total : 0) }), { total_overdue: 0 });

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
          <ArrowLeft className="w-3 h-3" /> Retour
        </button>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Loyers & Quittances</h1>
            <p className="text-sm mt-2 opacity-70">Suivi mensuel, relances et mises en demeure.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={regen} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2"><RefreshCw className="w-3 h-3"/> Générer mois manquants</button>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3 mb-6">
          <StatCard label="À échoir" value={stats.pending || 0} color="bg-slate-100" />
          <StatCard label="Payés" value={stats.paid || 0} color="bg-emerald-100" />
          <StatCard label="En retard" value={stats.late || 0} color="bg-yellow-100" />
          <StatCard label="EN DEMEURE" value={`${stats.overdue || 0} (${fmtMoney(stats.total_overdue)})`} color="bg-red-100" highlight />
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {["", "pending", "late", "overdue", "paid"].map(s => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s || "all"}`}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 border border-slate-900 ${filter === s ? "bg-slate-900 text-white" : "bg-white"}`}>
              {s ? STATUS[s].l : "Tous"}
            </button>
          ))}
        </div>

        <div className="bg-[color:var(--bg-surface)] border overflow-x-auto" style={{ borderColor: "var(--border-default)" }}>
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-3 py-3 text-left text-xs uppercase">Mois</th>
                <th className="px-3 py-3 text-left text-xs uppercase">Locataire</th>
                <th className="px-3 py-3 text-left text-xs uppercase">Local</th>
                <th className="px-3 py-3 text-left text-xs uppercase">Montant</th>
                <th className="px-3 py-3 text-left text-xs uppercase">Échéance</th>
                <th className="px-3 py-3 text-left text-xs uppercase">Statut</th>
                <th className="px-3 py-3 text-right text-xs uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan="7" className="p-8 text-center opacity-50">Chargement...</td></tr>}
              {!busy && items.length === 0 && <tr><td colSpan="7" className="p-12 text-center opacity-50 font-mono">Aucun loyer.</td></tr>}
              {items.map(r => (
                <tr key={r.id} className="border-t border-slate-900/10" data-testid={`row-rent-${r.id}`}>
                  <td className="px-3 py-3 font-mono">{r.month}</td>
                  <td className="px-3 py-3"><div className="font-semibold text-xs">{r.tenant?.name}</div><div className="text-xs opacity-50">{r.tenant?.email}</div>{r.tenant?.account_status === "blocked" && <span className="text-[10px] bg-red-200 text-red-900 px-1.5 py-0.5 font-bold uppercase mt-1 inline-block">Compte bloqué</span>}</td>
                  <td className="px-3 py-3 text-xs">{r.property?.title}</td>
                  <td className="px-3 py-3 text-xs"><div className="font-bold">{fmtMoney(r.amount_total)}</div><div className="opacity-50">{fmtMoney(r.amount_rent)}+{fmtMoney(r.amount_charges)} ch.</div></td>
                  <td className="px-3 py-3 text-xs font-mono">{r.due_date}</td>
                  <td className="px-3 py-3"><span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${STATUS[r.status]?.c || ""}`}>{STATUS[r.status]?.l || r.status}</span></td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {r.status !== "paid" ? (
                      <>
                        <button onClick={() => setMarkModal(r)} data-testid={`mark-paid-${r.id}`} title="Marquer payé" className="p-2 border border-slate-900 hover:bg-emerald-200 mr-1"><CheckCircle className="w-4 h-4"/></button>
                        <button onClick={() => sendReminder(r.id)} data-testid={`reminder-${r.id}`} title={r.status === "overdue" ? "Mise en demeure" : "Rappel"} className="p-2 border border-slate-900 hover:bg-yellow-200 mr-1"><Mail className="w-4 h-4"/></button>
                        {r.status === "overdue" && <button onClick={() => downloadDemand(r.id)} title="PDF Mise en demeure" className="p-2 border border-red-300 text-red-600 hover:bg-red-100"><FileText className="w-4 h-4"/></button>}
                      </>
                    ) : (
                      r.receipt_url && <button onClick={() => downloadReceipt(r.receipt_url)} data-testid={`receipt-${r.id}`} title="Télécharger quittance" className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white"><Download className="w-4 h-4"/></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {markModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setMarkModal(null)}>
            <div className="bg-white max-w-md w-full border border-slate-900" onClick={e => e.stopPropagation()} data-testid="mark-paid-modal">
              <div className="p-6 border-b border-slate-900/10">
                <h2 className="font-display font-black text-xl">Marquer comme payé</h2>
                <p className="text-sm opacity-60">Loyer {markModal.month} — {fmtMoney(markModal.amount_total)}</p>
              </div>
              <div className="p-6 space-y-4">
                <F label="Mode de paiement">
                  <select value={paid.payment_method} onChange={e => setPaid({...paid, payment_method: e.target.value})} className="input">
                    <option value="virement">Virement</option><option value="cheque">Chèque</option>
                    <option value="especes">Espèces</option><option value="stripe">Stripe</option><option value="autre">Autre</option>
                  </select>
                </F>
                <F label="Référence (optionnel)"><input value={paid.payment_reference} onChange={e => setPaid({...paid, payment_reference: e.target.value})} className="input" placeholder="N° virement, n° chèque..." /></F>
                <F label="Date paiement (vide = aujourd'hui)"><input type="date" value={paid.paid_at?.slice(0,10) || ""} onChange={e => setPaid({...paid, paid_at: e.target.value ? new Date(e.target.value).toISOString() : ""})} className="input" /></F>
              </div>
              <div className="p-6 border-t border-slate-900/10 flex justify-end gap-3">
                <button onClick={() => setMarkModal(null)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-sm font-bold uppercase tracking-wider">Annuler</button>
                <button onClick={markPaid} data-testid="mark-paid-confirm" className="btn-brutal btn-brutal-solar px-5 py-2 text-sm font-bold uppercase tracking-wider">Valider & quittance</button>
              </div>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const StatCard = ({ label, value, color, highlight }) => (
  <div className={`${color} p-4 border ${highlight ? "border-red-600 border-2" : "border-slate-900/15"}`}>
    <div className="text-xs font-bold uppercase tracking-widest opacity-60">{label}</div>
    <div className={`font-display font-black ${highlight ? "text-2xl" : "text-3xl"} mt-1`}>{value}</div>
  </div>
);

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
